import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Search, FileText, Send, ChevronLeft, ChevronRight, AlertTriangle, ChevronsLeft, ChevronsRight } from "lucide-react";
import { ClientTitlesDialog } from "@/components/ClientTitlesDialog";

const MONTH_NAMES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const PAGE_SIZE = 25;

function overdueBadge(dias) {
  if (dias >= 30) return "bg-red-500/15 text-red-300 border border-red-500/30";
  if (dias >= 10) return "bg-orange-500/15 text-orange-300 border border-orange-500/30";
  return "bg-amber-500/15 text-amber-300 border border-amber-500/30";
}

export default function Clients() {
  const [tab, setTab] = useState("all"); // "all" | "overdue"

  // -------- aba: todos os clientes --------
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);

  const reqId = useRef(0);
  const load = async (q = search, p = 1) => {
    const id = ++reqId.current;
    setLoading(true);
    try {
      const { data } = await api.get("/clients", { params: { search: q, page: p, limit: PAGE_SIZE } });
      if (id === reqId.current) {
        setRows(data.items || []);
        setPage(data.page || 1);
        setPages(data.pages || 1);
        setTotal(data.total || 0);
      }
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  };
  useEffect(() => {
    load("", 1);
  }, []);

  // -------- aba: em atraso --------
  const now = new Date();
  const [refDate, setRefDate] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });
  const [overdue, setOverdue] = useState(null);
  const [overdueLoading, setOverdueLoading] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [overdueSearch, setOverdueSearch] = useState("");
  const [overduePage, setOverduePage] = useState(1);

  const loadOverdue = async (year, month) => {
    setOverdueLoading(true);
    try {
      const { data } = await api.get("/clients/overdue", { params: { year, month } });
      setOverdue(data);
      setOverduePage(1);
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setOverdueLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "overdue") loadOverdue(refDate.year, refDate.month);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, refDate]);

  const shiftMonth = (delta) => {
    setRefDate((r) => {
      let m = r.month + delta;
      let y = r.year;
      if (m < 1) { m = 12; y -= 1; }
      if (m > 12) { m = 1; y += 1; }
      return { year: y, month: m };
    });
  };

  const isCurrentMonth = refDate.year === now.getFullYear() && refDate.month === now.getMonth() + 1;

  const overdueFiltered = (overdue?.items || []).filter((it) => {
    if (!overdueSearch.trim()) return true;
    const q = overdueSearch.trim().toLowerCase();
    return (
      (it.nome || "").toLowerCase().includes(q) ||
      (it.login || "").toLowerCase().includes(q) ||
      (it.celular || "").includes(q) ||
      (it.cpf || "").includes(q)
    );
  });
  const overdueValorFiltrado = overdueFiltered.reduce(
    (acc, it) => acc + (parseFloat(String(it.valor).replace(",", ".")) || 0), 0
  );
  const overduePageCount = Math.max(1, Math.ceil(overdueFiltered.length / PAGE_SIZE));
  const overduePageSafe = Math.min(overduePage, overduePageCount);
  const overduePageItems = overdueFiltered.slice((overduePageSafe - 1) * PAGE_SIZE, overduePageSafe * PAGE_SIZE);

  useEffect(() => {
    setOverduePage(1);
  }, [overdueSearch]);

  const sendOverdueCharge = async (item) => {
    setSendingId(item.title_id);
    try {
      await api.post(`/clients/${item.login}/send-charge`, { title_id: String(item.title_id) });
      toast.success(`Cobrança enviada pra ${item.nome}`);
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setSendingId(null);
    }
  };

  return (
    <div className="space-y-8">
      <header className="border-b border-slate-800 pb-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400">mk-auth</div>
        <h1 className="font-outfit text-4xl font-bold tracking-tight text-white mt-1">Clientes</h1>
        <p className="text-sm text-slate-500 mt-2">
          Dados lidos diretamente da tabela <span className="font-mono-jb">clientes</span> do mk-auth.
        </p>
      </header>

      {/* -------- Abas -------- */}
      <div className="flex gap-2">
        <button
          data-testid="clients-tab-all"
          onClick={() => setTab("all")}
          className={`text-sm px-4 py-2 rounded-full border transition-colors ${
            tab === "all" ? "border-emerald-500 bg-emerald-500/15 text-emerald-300" : "border-slate-700 text-slate-400 hover:text-slate-300"
          }`}
        >
          Todos os clientes
        </button>
        <button
          data-testid="clients-tab-overdue"
          onClick={() => setTab("overdue")}
          className={`text-sm px-4 py-2 rounded-full border flex items-center gap-1.5 transition-colors ${
            tab === "overdue" ? "border-red-500 bg-red-500/15 text-red-300" : "border-slate-700 text-slate-400 hover:text-slate-300"
          }`}
        >
          <AlertTriangle className="w-3.5 h-3.5" /> Em Atraso
        </button>
      </div>

      {tab === "all" && (
        <Card className="bg-slate-900/50 border-slate-800 p-6">
          <div className="flex gap-3">
            <Input
              data-testid="clients-search-input"
              placeholder="Buscar por nome, login, cpf, celular…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load(search, 1)}
              className="bg-slate-950 border-slate-800 text-white focus-visible:ring-emerald-500/40 focus-visible:border-emerald-500"
            />
            <Button
              data-testid="clients-search-btn"
              onClick={() => load(search, 1)}
              className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
            >
              <Search className="w-4 h-4 mr-2" /> Buscar
            </Button>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
                <tr>
                  <th className="text-left py-2 px-2">Login</th>
                  <th className="text-left py-2 px-2">Nome</th>
                  <th className="text-left py-2 px-2">CPF</th>
                  <th className="text-left py-2 px-2">Celular</th>
                  <th className="text-left py-2 px-2">Cidade</th>
                  <th className="text-right py-2 px-2"></th>
                </tr>
              </thead>
              <tbody data-testid="clients-tbody">
                {loading && (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-slate-500">
                      Carregando…
                    </td>
                  </tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-slate-500">
                      Sem registros. Verifique a conexão do mk-auth em <span className="text-emerald-400">Configurações</span>.
                    </td>
                  </tr>
                )}
                {rows.map((r, i) => (
                  <tr
                    key={r.login || i}
                    className="border-b border-slate-800/60 hover:bg-slate-800/40 transition-colors duration-200"
                  >
                    <td className="py-2 px-2 font-mono-jb text-emerald-300">{r.login}</td>
                    <td className="py-2 px-2 text-white">{r.nome}</td>
                    <td className="py-2 px-2 font-mono-jb text-slate-400">{r.cpf || r.cnpj}</td>
                    <td className="py-2 px-2 font-mono-jb text-slate-400">{r.celular}</td>
                    <td className="py-2 px-2 text-slate-400">{r.cidade}</td>
                    <td className="py-2 px-2 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`client-titles-btn-${r.login}`}
                        onClick={() => setSelected(r.login)}
                        className="border-slate-700 hover:bg-slate-800 hover:text-white text-xs"
                      >
                        <FileText className="w-3.5 h-3.5 mr-1.5" /> Boletos
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {total > 0 && (
            <div className="flex items-center justify-between pt-4 mt-2 border-t border-slate-800 flex-wrap gap-2">
              <div className="text-xs text-slate-500">
                Página {page} de {pages} · {total} cliente(s) no total
              </div>
              <div className="flex items-center gap-1">
                <button
                  data-testid="clients-page-first"
                  onClick={() => load(search, 1)}
                  disabled={page === 1}
                  className="text-slate-400 hover:text-emerald-300 p-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronsLeft className="w-4 h-4" />
                </button>
                <button
                  data-testid="clients-page-prev"
                  onClick={() => load(search, Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="text-slate-400 hover:text-emerald-300 p-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-xs text-slate-400 px-2 font-mono-jb">{page} / {pages}</span>
                <button
                  data-testid="clients-page-next"
                  onClick={() => load(search, Math.min(pages, page + 1))}
                  disabled={page === pages}
                  className="text-slate-400 hover:text-emerald-300 p-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button
                  data-testid="clients-page-last"
                  onClick={() => load(search, pages)}
                  disabled={page === pages}
                  className="text-slate-400 hover:text-emerald-300 p-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronsRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </Card>
      )}

      {tab === "overdue" && (
        <Card className="bg-slate-900/50 border-slate-800 p-0 overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <button onClick={() => shiftMonth(-1)} className="text-slate-400 hover:text-emerald-300 p-1" data-testid="overdue-prev-month">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="text-white font-outfit font-semibold text-lg min-w-[160px] text-center">
                {MONTH_NAMES[refDate.month - 1]} / {refDate.year}
                {isCurrentMonth && <span className="text-[10px] uppercase tracking-wider text-emerald-400 ml-2 align-middle">mês atual</span>}
              </div>
              <button
                onClick={() => shiftMonth(1)}
                disabled={isCurrentMonth}
                className="text-slate-400 hover:text-emerald-300 p-1 disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="overdue-next-month"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            {overdue && (
              <div className="text-sm text-slate-400">
                {overdueFiltered.length} cliente(s){overdueSearch.trim() && ` de ${overdue.total}`} em atraso ·{" "}
                <span className="text-red-300 font-mono-jb">R$ {overdueValorFiltrado.toFixed(2).replace(".", ",")}</span> em aberto
              </div>
            )}
          </div>

          <div className="px-6 py-3 border-b border-slate-800/60">
            <Input
              data-testid="overdue-search-input"
              value={overdueSearch}
              onChange={(e) => setOverdueSearch(e.target.value)}
              placeholder="Buscar por nome, login, celular ou CPF…"
              className="bg-slate-950 border-slate-800 text-white max-w-sm focus-visible:ring-emerald-500/40 focus-visible:border-emerald-500"
            />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
                <tr>
                  <th className="text-left py-3 px-4">Cliente</th>
                  <th className="text-left py-3 px-4">Celular</th>
                  <th className="text-left py-3 px-4">Venceu em</th>
                  <th className="text-left py-3 px-4">Atraso</th>
                  <th className="text-left py-3 px-4">Valor</th>
                  <th className="text-left py-3 px-4">Ações</th>
                </tr>
              </thead>
              <tbody data-testid="overdue-tbody">
                {overdueLoading && (
                  <tr><td colSpan={6} className="py-8 text-center text-slate-500">Carregando…</td></tr>
                )}
                {!overdueLoading && overdueFiltered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-10 text-center text-slate-500">
                      {overdueSearch.trim()
                        ? "Nenhum cliente encontrado com esse filtro."
                        : `Nenhum cliente em atraso em ${MONTH_NAMES[refDate.month - 1]}/${refDate.year}. 🎉`}
                    </td>
                  </tr>
                )}
                {!overdueLoading && overduePageItems.map((it) => (
                  <tr key={it.title_id} className="border-b border-slate-800/60 hover:bg-slate-800/40">
                    <td className="py-2.5 px-4 text-white">
                      {it.nome}
                      <div className="text-[11px] text-slate-500 font-mono-jb">{it.login}</div>
                    </td>
                    <td className="py-2.5 px-4 font-mono-jb text-slate-400 text-xs">{it.celular || "—"}</td>
                    <td className="py-2.5 px-4 font-mono-jb text-slate-400 text-xs">
                      {it.datavenc ? new Date(it.datavenc + "T00:00:00").toLocaleDateString("pt-BR") : "—"}
                    </td>
                    <td className="py-2.5 px-4">
                      <Badge className={overdueBadge(it.dias_atraso)}>{it.dias_atraso}d</Badge>
                    </td>
                    <td className="py-2.5 px-4 font-mono-jb text-emerald-300">
                      R$ {parseFloat(String(it.valor).replace(",", ".")).toFixed(2).replace(".", ",")}
                    </td>
                    <td className="py-2.5 px-4">
                      <Button
                        size="sm"
                        data-testid={`send-overdue-btn-${it.title_id}`}
                        onClick={() => sendOverdueCharge(it)}
                        disabled={sendingId === it.title_id}
                        className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold text-xs h-8"
                      >
                        <Send className="w-3.5 h-3.5 mr-1.5" />
                        {sendingId === it.title_id ? "Enviando…" : "Enviar cobrança"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {overdueFiltered.length > 0 && (
            <div className="flex items-center justify-between px-6 py-3 border-t border-slate-800 flex-wrap gap-2">
              <div className="text-xs text-slate-500">
                Página {overduePageSafe} de {overduePageCount} · mostrando{" "}
                {(overduePageSafe - 1) * PAGE_SIZE + 1}–{Math.min(overduePageSafe * PAGE_SIZE, overdueFiltered.length)} de{" "}
                {overdueFiltered.length}
              </div>
              <div className="flex items-center gap-1">
                <button
                  data-testid="overdue-page-first"
                  onClick={() => setOverduePage(1)}
                  disabled={overduePageSafe === 1}
                  className="text-slate-400 hover:text-emerald-300 p-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronsLeft className="w-4 h-4" />
                </button>
                <button
                  data-testid="overdue-page-prev"
                  onClick={() => setOverduePage((p) => Math.max(1, p - 1))}
                  disabled={overduePageSafe === 1}
                  className="text-slate-400 hover:text-emerald-300 p-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-xs text-slate-400 px-2 font-mono-jb">{overduePageSafe} / {overduePageCount}</span>
                <button
                  data-testid="overdue-page-next"
                  onClick={() => setOverduePage((p) => Math.min(overduePageCount, p + 1))}
                  disabled={overduePageSafe === overduePageCount}
                  className="text-slate-400 hover:text-emerald-300 p-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button
                  data-testid="overdue-page-last"
                  onClick={() => setOverduePage(overduePageCount)}
                  disabled={overduePageSafe === overduePageCount}
                  className="text-slate-400 hover:text-emerald-300 p-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronsRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </Card>
      )}

      <ClientTitlesDialog login={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
